import { el, btn } from '@/ui/dom';
import { setBackNavModal } from '@/ui/backNav';

/**
 * confirm.ts — THE "Are you sure?" step (M115, owner rule).
 *
 * Every destructive action in the game routes through ONE affordance so a
 * player always sees the same shape of question before something is lost:
 * a modal over the screen, plain English about what happens and what goes
 * away, a RED confirm and a NEUTRAL cancel (the M92 colour rule), safe by
 * default (focus starts on CANCEL, Esc cancels, tapping the scrim cancels).
 *
 * It replaces the old inline two-tap "…— SURE?" pattern, which relabelled the
 * same button and silently timed out — easy to miss, easy to double-tap
 * through, and it never said what would actually be lost.
 *
 * There is only ever ONE dialog on screen: opening a second replaces the first.
 */
export interface ConfirmOptions {
  /** Short question in the display voice, e.g. 'SIGN OUT?'. */
  title: string;
  /** Plain English: what happens, and what is lost. One or two sentences. */
  body: string;
  /** The red button's label — name the ACTION, e.g. 'DELETE FOREVER'. */
  confirmLabel: string;
  /** Defaults to 'CANCEL'. */
  cancelLabel?: string;
  /** Run only on a real confirm. Cancelling does nothing at all. */
  onConfirm: () => void;
  /** Park the entrance animation (the Settings reduced-motion switch). */
  reducedMotion?: boolean;
}

/** The live dialog, if any (module-level: only one can ever be open). */
let open: {
  veil: HTMLDivElement;
  onKey: (e: KeyboardEvent) => void;
  restore: Element | null;
} | null = null;

/** Tear the current dialog down. Called by cancel, confirm, and screen hide(). */
export function closeConfirm(): void {
  if (!open) return;
  const { veil, onKey, restore } = open;
  open = null;
  document.removeEventListener('keydown', onKey, true);
  veil.remove();
  // The screen underneath can swipe again (M114b edge-back).
  setBackNavModal(false);
  if (restore instanceof HTMLElement && document.contains(restore)) restore.focus();
}

/** True while a confirm is up (so a screen can tell whether to clean up). */
export function confirmOpen(): boolean {
  return open !== null;
}

export function confirmAction(opts: ConfirmOptions): void {
  closeConfirm(); // never stack two questions
  const restore = document.activeElement;

  const veil = el('div', 'confirm-veil', document.body);
  veil.classList.toggle('reduced', !!opts.reducedMotion);
  veil.setAttribute('role', 'alertdialog');
  veil.setAttribute('aria-modal', 'true');

  const panel = el('div', 'panel confirm-panel', veil);
  const kick = el('div', 'mhead-kicker confirm-kicker', panel);
  el('span', 'mhead-rule', kick);
  el('span', 'mhead-kicker-text', kick).textContent = 'ARE YOU SURE?';
  el('span', 'mhead-rule', kick);

  const title = el('div', 'confirm-title', panel);
  title.textContent = opts.title;
  title.id = 'confirm-title';
  veil.setAttribute('aria-labelledby', title.id);

  const body = el('div', 'confirm-body', panel);
  body.textContent = opts.body;
  body.id = 'confirm-body';
  veil.setAttribute('aria-describedby', body.id);

  // CANCEL first in the DOM so the safe choice leads both the reading order
  // and the tab order; the grid puts it on the left, the red action right.
  const actions = el('div', 'confirm-actions', panel);
  const cancelBtn = btn(
    opts.cancelLabel ?? 'CANCEL',
    'btn btn-secondary confirm-cancel',
    actions,
    () => closeConfirm(),
  );
  const goBtn = btn(opts.confirmLabel, 'btn btn-danger confirm-go', actions, () => {
    closeConfirm();
    opts.onConfirm();
  });

  // Tapping the backdrop (but never the panel) is a cancel, like every modal.
  veil.addEventListener('pointerdown', (e) => {
    if (e.target === veil) closeConfirm();
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // never let Esc ALSO close the screen behind us
      closeConfirm();
      return;
    }
    if (e.key === 'Tab') {
      // Two focusables, trapped: the player cannot tab out into the screen
      // behind the modal and press something they cannot see.
      e.preventDefault();
      (document.activeElement === cancelBtn ? goBtn : cancelBtn).focus();
    }
  };
  document.addEventListener('keydown', onKey, true);

  open = { veil, onKey, restore };
  // Freeze the edge swipe-back while the question is up — a modal must be modal.
  setBackNavModal(true);
  cancelBtn.focus(); // safe default
}
